import { type Settings } from "@core/shared/types";
import { type Location } from "@remix-run/react";

export type PageHead = {
  title: string;
  description?: string;
  author?: {
    name: string;
    image?: string;
  };
  appendAppName?: boolean;
  image?: string;
  type?: "website" | "article" | "profile" | "book";
  location?: Location;
  keywords?: string[];
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  siteName?: string;
  locale?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  contactPhone?: string; // For contact pages
  settings?: Settings; // Settings from root loader
};

type HeadData = {
  title?: string;
  name?: string;
  content?: string;
  property?: string;
  rel?: string;
}[];

// Helper function to get root settings from meta function matches
export function getRootSettings(matches: any[]): Settings {
  const rootData = matches.find((match) => match.id === "root")?.data;

  return rootData?.settings || {};
}

function og(name: string, content: string) {
  return {
    property: `og:${name}`,
    content,
  };
}

function twitter(name: string, content: string) {
  return {
    name: `twitter:${name}`,
    content,
  };
}

/**
 * Generate comprehensive meta tags for SEO and social media sharing
 * @param data - Page metadata configuration
 * @param data.title - Page title
 * @param data.appendAppName - Whether to append site name to title (e.g., "Page Title | Site Name")
 * @param data.settings - Settings from root loader (required for appendAppName)
 * @returns Array of meta tag objects
 */
export function pageHead(data: PageHead) {
  const headData: HeadData = [];

  // Default values
  const type = data.type || "website";
  const siteName =
    data.siteName || data.settings?.general?.appName || "Mentor Me";
  const locale = data.locale || "en_US";

  // Handle title with app name appending
  let finalTitle = data.title;
  if (data.appendAppName && data.settings?.general?.appName) {
    finalTitle = `${data.title} | ${data.settings.general.appName}`;
  }

  // Title generation
  metaTitleGenerator(finalTitle, headData);

  // Basic Open Graph tags
  headData.push(
    og("type", type),
    og("site_name", siteName),
    og("locale", locale),
  );

  // URL handling
  if (data.location) {
    const baseUrl = data.settings?.general?.url;
    if (baseUrl) {
      const fullUrl = `${baseUrl}${data.location.pathname}${data.location.search}`;
      headData.push(og("url", fullUrl), {
        rel: "canonical",
        content: data.canonicalUrl || fullUrl,
      });
    }
  }

  // Author information
  if (data.author?.name) {
    headData.push(
      og("author", data.author.name),
      twitter("creator", data.author.name),
    );

    // Author image for social media
    if (data.author.image) {
      headData.push(
        og("author:image", data.author.image),
        twitter("creator:image", data.author.image),
      );
    }
  }

  // Contact phone number for contact pages
  if (data.contactPhone) {
    headData.push(
      meta("contact:phone", data.contactPhone),
      og("contact:phone", data.contactPhone),
    );
  }

  // Keywords
  if (data.keywords && Array.isArray(data.keywords)) {
    headData.push({
      name: "keywords",
      content: data.keywords.join(", "),
    });
  }

  // Description
  metaDescriptionGenerator(data.description || "", headData);

  // Image handling
  metaImageGenerator(data.image || "", headData);

  // Article-specific meta tags
  if (type === "article") {
    articleMetaGenerator(data, headData);
  }

  // Twitter Card
  twitterCardGenerator(data, headData);

  // SEO directives
  seoDirectivesGenerator(data, headData);

  return headData;
}

export function metaDescriptionGenerator(
  description: string,
  headData: HeadData,
) {
  if (!description) return;

  headData.push(
    // Standard description
    meta("description", description),
    // Open Graph description
    og("description", description),
    // Twitter description
    twitter("description", description),
  );
}

export function metaTitleGenerator(title: string, headData: HeadData) {
  headData.push({ title });

  headData.push(
    // Open Graph title
    og("title", title),
    // Twitter title
    twitter("title", title),
  );
}

function meta(name: string, content: string) {
  return {
    name,
    content,
  };
}

function property(name: string, content: string) {
  return {
    property: name,
    content,
  };
}

export function metaImageGenerator(imagePath: string, headData: HeadData) {
  if (!imagePath) return;

  headData.push(
    // Open Graph image
    property("og:image", imagePath),
    property("og:image:width", "1200"),
    property("og:image:height", "630"),
    property("og:image:type", "image/jpeg"),
    property("og:image:alt", "Page preview image"),
    // Twitter image
    property("twitter:image", imagePath),
    property("twitter:image:alt", "Page preview image"),
  );
}

function articleMetaGenerator(data: PageHead, headData: HeadData) {
  if (data.publishedTime) {
    headData.push(
      og("article:published_time", data.publishedTime),
      meta("article:published_time", data.publishedTime),
    );
  }

  if (data.modifiedTime) {
    headData.push(
      og("article:modified_time", data.modifiedTime),
      meta("article:modified_time", data.modifiedTime),
    );
  }

  if (data.author?.name) {
    headData.push(
      og("article:author", data.author.name),
      meta("article:author", data.author.name),
    );
  }

  if (data.section) {
    headData.push(
      og("article:section", data.section),
      meta("article:section", data.section),
    );
  }

  if (data.tags && Array.isArray(data.tags)) {
    data.tags.forEach((tag) => {
      headData.push(og("article:tag", tag), meta("article:tag", tag));
    });
  }
}

function twitterCardGenerator(data: PageHead, headData: HeadData) {
  // Determine Twitter Card type based on content
  const hasImage = data.image || data.author?.image;
  const cardType = hasImage ? "summary_large_image" : "summary";

  headData.push(
    twitter("card", cardType),
    twitter("site", "@mentor_me"), // Replace with actual Twitter handle
  );
}

function seoDirectivesGenerator(data: PageHead, headData: HeadData) {
  if (data.noIndex) {
    headData.push(meta("robots", "noindex"));
  }

  if (data.noFollow) {
    headData.push(meta("robots", "nofollow"));
  }

  if (data.noIndex && data.noFollow) {
    headData.push(meta("robots", "noindex, nofollow"));
  }

  // Default robots directive if none specified
  if (!data.noIndex && !data.noFollow) {
    headData.push(meta("robots", "index, follow"));
  }
}
